import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    InjectionToken,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { NewsFeedModel } from '../news-feed.model';
import { PostModel } from '@fi-shared/models';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { AccessRightService } from '@fi-shared/fi-shared-services/access-right.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { AccessRightFilterSettings } from '@fi-shared/access-right-filters';
import { NewsFeedService } from '../news-feed.service';
import { NewsFeedItemEditComponent } from '../news-feed-item-edit/news-feed-item-edit.component';
import { ModuleType } from '@fi-shared/types/module-type';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { ActionType } from '@fi-shared/types/action-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AppTextService } from '@fi-shared/app-text';
import { Subject, Subscription } from 'rxjs';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { IItemUpdatedEventArgs, IItemUpdatedEventData } from '@fi-shared/fi-signal-r/fi-signal-r';
import { FiNewsFeedSharedService } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared.service';
import { takeUntil } from 'rxjs/operators';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { VISIBILITY_HELPER, VisibilityHelper } from '@fi-shared/fi-directives/become-visible/visibility-helper';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { findClosest, isDefined } from '@fi-shared/utils';
import { removeItem } from '@fi-shared/utils/remove-item';
import { INewsFeedModel } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiSharedActionsService } from '@fi-app/utilities/fi-services/fi-shared-actions.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemUpdatedEventType } from '@fi-shared/fi-signal-r/fi-item-update-event-type';

export interface INewsFeedData {
    showForumActivity?: boolean;
    newsFeedHeaderText?: string;
    newsFeedHeaderSref?: string;
    newsFeedHeaderSrefParams?: {
        [prop: string]: any;
    };
    commentId?: number;
    objectType?;
    objectId?: number;
    forumId?: number;
    noHighlight?: boolean;
    NumberOfRecordsToTake?: number;
    showAllForumPostsIfAdmin?: boolean;
}

export const NEWS_FEED_CHILD_REGISTER = new InjectionToken<(component: NewsFeedComponent) => void>(
    'NEWS_FEED_CHILD_REGISTER',
);

@Component({
    selector: 'fi-news-feed',
    templateUrl: './news-feed.component.html',
    styleUrls: ['./news-feed.component.scss', '../../components/items-list/items-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [
        {
            provide: VISIBILITY_HELPER,
            useFactory: (newsFeedComponent: NewsFeedComponent) => {
                return newsFeedComponent.visibilityHelper;
            },
            deps: [NewsFeedComponent],
        },
    ],
    standalone: false,
})
export class NewsFeedComponent implements OnInit, OnChanges, OnDestroy {
    @Input() initialNewsFeedResponse: INewsFeedModel[];
    @Input() newsFeedData: INewsFeedData;
    @Input() hasAccessToModule: boolean;
    @Output() initialRenderDone = new EventEmitter();

    onWewPostEditorSet = new EventEmitter<NewsFeedItemEditComponent>();
    // Once new post editor initialized - check state and open mobile editor if needed
    @ViewChild('newPostEditor', { static: false }) set newPostEditor(content: NewsFeedItemEditComponent) {
        this.onWewPostEditorSet.emit(content);
    }

    newsFeedItems: NewsFeedModel[] = [];
    private newItems: IItemUpdatedEventArgs[] = [];
    private lastNewItem: IItemUpdatedEventArgs;
    newItemsCount: number = 0;

    showForumData: boolean;
    CanCreatePost: boolean = false;
    Post: PostModel = new PostModel(this.fiFormatDateService);
    AccessRightFilterSettings: AccessRightFilterSettings;
    WriteNewPost: boolean = false;

    visibilityHelper: VisibilityHelper;

    readonly commentsShowLimit = 4;

    canLoadMore = false;
    private renderDone = false;
    private documentClickSubscription: Subscription;

    constructor(
        private appTextService: AppTextService,
        private fiLoadingService: LoadingService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private accessRightService: AccessRightService,
        private statisticService: StatisticService,
        private newsFeedService: NewsFeedService,
        private newsFeedSharedService: FiNewsFeedSharedService,
        private fiSignalRService: FiSignalRService,
        private stateService: FiStateService,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private userDataService: FiUserDataService,
        private elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private fiFormatDateService: FiFormatDateService,
        private sharedActionsService: FiSharedActionsService,
        @Inject(NEWS_FEED_CHILD_REGISTER) @Optional() private newsFeedChildRegister?,
    ) {}

    ngOnInit() {
        if (this.newsFeedChildRegister) this.newsFeedChildRegister(this);

        this.newsFeedService.loadNewNewsFeedItems$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.lastNewItem = null;
            if (!this.newItems.length) return;

            this.lastNewItem = this.newItems[this.newItems.length - 1];
            this.doLoadNewNewsFeedItems(this.newItems);
        });

        this.fiSignalRService.itemUpdatedEvent.subscribe((data) => this.onItemUpdated(data), this.ngOnDestroy$);
        this.fiSignalRService.dbOperationEvent.subscribe(this.onDbOperation.bind(this), this.ngOnDestroy$);
        this.AccessRightFilterSettings = new AccessRightFilterSettings(ModuleType.Post, false, false, ObjectTypes.Post);
        //this.AccessRightFilterSettings.SelectionMode = AccessRightTabs.QuickChoice;

        this.newsFeedService.homeTabScrolled$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.LoadMoreNewsItems();
        });

        this.visibilityHelper = new VisibilityHelper(this.elementRef.nativeElement);

        this.statisticService.Visit(ModuleType.NewsFeed);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.initialNewsFeedResponse;
        if (change && change.currentValue) {
            this.canLoadMore =
                this.initialNewsFeedResponse.length > 0 && !isDefined(this.newsFeedData.NumberOfRecordsToTake);
            this.renderDone = false;
            this.newsFeedItems = [];
            this.newItems = [];
            this.processNewsFeedResponse(this.initialNewsFeedResponse);

            this.showForumData = !this.newsFeedData.forumId;

            if (this.newsFeedData.forumId) {
                this.Post.IsForumPost = true;
                this.Post.ForumId = this.newsFeedData.forumId;
            }

            this.CanCreatePost = (() => {
                if (this.newsFeedData.forumId)
                    return this.accessToApplicationTypeService.HasAccessToApplicationType(
                        ApplicationSettingsType.Forum,
                        AccessRightType.Write,
                    );
                return (
                    this.accessToApplicationTypeService.HasAccessToApplicationType(
                        ApplicationSettingsType.PostCentral,
                        AccessRightType.Write,
                    ) ||
                    this.accessToApplicationTypeService.HasAccessToApplicationType(
                        ApplicationSettingsType.PostLocal,
                        AccessRightType.Write,
                    )
                );
            })();

            setTimeout(() => this.itemsRenderDone()); //todo in proper place
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.visibilityHelper.onDestroy();
        this.unsubscribeClickSubscription();
    }

    private loadingItems = false;
    LoadMoreNewsItems() {
        if (this.canLoadMore && !this.loadingItems) {
            this.loadingItems = true;
            this.newsFeedService
                .GetNewsFeeds(this.NewsItemsOffset(), this.newsFeedData)
                .then((response) => {
                    this.canLoadMore = response.length > 0;
                    this.processNewsFeedResponse(response);
                })
                .finally(() => {
                    this.loadingItems = false;
                });
        }
    }

    private readonly brandedBorderColorClassName: string = 'BrandedBorderColorHalf';
    private readonly removeClassDelay: number = 2000;
    private readonly newsFeedPostContentClassSelector = '.newsfeed-post-content';
    itemsRenderDone() {
        if (!this.renderDone) {
            this.renderDone = true;
            if (this.newsFeedData.objectId) {
                const postElement: HTMLElement = document.getElementById(
                    `NewsFeedItem-${this.newsFeedData.objectId}-${this.newsFeedData.objectType}`,
                );
                if (this.newsFeedData.commentId) {
                    const comment: HTMLElement = document.getElementById(`comment ${this.newsFeedData.commentId}`);
                    const mainContentViewHeight: number = document.getElementById('main-content-view').offsetHeight;

                    setTimeout(() => {
                        const currentElement =
                            postElement.offsetHeight <= mainContentViewHeight ? postElement : comment;
                        currentElement.scrollIntoView();
                    });

                    // highlight selected comment
                    this.renderer.addClass(comment, this.brandedBorderColorClassName);
                    setTimeout(
                        () => this.renderer.removeClass(comment, this.brandedBorderColorClassName),
                        this.removeClassDelay,
                    );
                } else {
                    setTimeout(() => {
                        if (postElement) postElement.scrollIntoView();
                    });
                    if (!this.newsFeedData.noHighlight) {
                        // highlight selected post
                        this.renderer.addClass(
                            postElement.querySelector(this.newsFeedPostContentClassSelector),
                            this.brandedBorderColorClassName,
                        );

                        setTimeout(() => {
                            this.renderer.removeClass(
                                postElement.querySelector(this.newsFeedPostContentClassSelector),
                                this.brandedBorderColorClassName,
                            );
                        }, this.removeClassDelay);
                    }
                }
            }
            this.initialRenderDone.emit();
        }
    }

    onItemUpdated(newsItem: IItemUpdatedEventArgs) {
        if (
            !this.newsFeedData ||
            newsItem.EventType !== FiItemUpdatedEventType.Feed ||
            this.newsFeedService.isNewsItemObjectTypeIgnored(newsItem.ObjectType)
        )
            return;

        if (!this.newsFeedData.showForumActivity && newsItem.ForumId !== this.newsFeedData.forumId) return;

        if (
            newsItem.EventByUserId === this.userDataService.getUserId() &&
            this.newsFeedData.showForumActivity &&
            newsItem.ForumId &&
            !this.newsFeedItems.filter((item) => {
                return newsItem.ObjectId === item.ObjectId && newsItem.ObjectType === item.ObjectType;
            }).length
        ) {
            // https://chainformation.atlassian.net/browse/FI-990
            // If I create a post in a forum, then the post does not appear in the regular feed for me.
            // When I create a forum post, the post should automatically be displayed in my regular feed as well.
            // But it shoult NOT generate a new badge on the home button and NO "x new event" button should
            // appear since I am the creator of the post.
            this.doLoadNewNewsFeedItems([newsItem], true);
        } else if (newsItem.EventByUserId !== this.userDataService.getUserId()) {
            const isPublished = newsItem.ActionType !== ActionType.Delete;
            const existingItems = this.newItems.filter((item) => {
                return newsItem.ObjectId === item.ObjectId && newsItem.ObjectType === item.ObjectType;
            });
            if (isPublished && !existingItems.length && newsItem.ObjectType !== ObjectTypes.Notification) {
                this.newItems.push(newsItem);
            } else if (!isPublished && existingItems.length) {
                this.newItems.splice(this.newItems.indexOf(existingItems[0]), 1);
            }
            this.cdr.markForCheck();
            this.updateNewItemsCount();
        }
    }

    onDbOperation(data: IItemUpdatedEventData) {
        if (data.Action == ActionType.Delete) {
            const newNewsItemsToRemove = this.newItems.filter((item) => {
                return item.ObjectId == data.Id && item.ObjectType == data.ObjectType;
            });
            if (newNewsItemsToRemove.length) removeItem(this.newItems, newNewsItemsToRemove[0]);

            this.updateNewItemsCount();
        }
    }

    updateNewItemsCount() {
        const newCount = this.newItems.length;
        if (this.newItemsCount !== newCount) this.newItemsCount = newCount;
    }

    processNewsFeedResponse(response: INewsFeedModel[], addToTop?: boolean) {
        if (response) {
            const newsFeedsToAdd: NewsFeedModel[] = [];
            response.forEach((newsFeed) => {
                newsFeedsToAdd.push(
                    new NewsFeedModel(
                        newsFeed,
                        this.userDataService.userData.PreferredContentLanguage,
                        this.fiFormatDateService,
                    ),
                );
            });

            newsFeedsToAdd.forEach((newsFeed) => {
                if (!addToTop) {
                    this.newsFeedItems.push(newsFeed);
                } else {
                    const firstUnpinnedItemIdx = this.newsFeedItems.findIndex((item) => !item.PinnedDate);
                    if (firstUnpinnedItemIdx !== -1) {
                        this.newsFeedItems.splice(firstUnpinnedItemIdx, 0, newsFeed);
                    } else {
                        this.newsFeedItems.push(newsFeed);
                    }
                }
            });

            this.cdr.markForCheck();
            //this.NewsItems = addToTop ? newsFeedsToAdd.concat(this.NewsItems) : this.NewsItems.concat(newsFeedsToAdd);

            /*// get delete access data
            this.newsFeedService.updateDeleteAccess(newsFeedsToAdd);*/
        }
    }

    getScrollableElementSelector() {
        if (this.newsFeedData.forumId) return '#forum-view-layout';

        return '#start-page-layout';
    }
    shouldScrollIntoItemView(newsFeedItem: NewsFeedModel) {
        return (
            !!this.lastNewItem &&
            this.lastNewItem.ObjectId === newsFeedItem.ObjectId &&
            this.lastNewItem.ObjectType === newsFeedItem.ObjectType
        );
    }

    NewsItemsOffset() {
        return this.newsFeedSharedService.getObjectItems(this.newsFeedItems).length;
    }

    private doLoadNewNewsFeedItems(items: IItemUpdatedEventArgs[], skipReplace?: boolean) {
        const promise = this.newsFeedService.GetNewsFeedsByIdType(items).then((response: INewsFeedModel[]) => {
            // remove from existing NewsFeeds updated items (received from server)
            response.forEach((newItem: INewsFeedModel) => {
                const existingItems = this.newsFeedItems.filter((item) => {
                    return newItem.ObjectId == item.ObjectId && newItem.ObjectType == item.ObjectType;
                });
                if (existingItems.length) {
                    if (!skipReplace) {
                        this.newsFeedItems.splice(this.newsFeedItems.indexOf(existingItems[0]), 1);
                    } else {
                        response.splice(response.indexOf(newItem), 1);
                    }
                    this.cdr.markForCheck();
                }
            });

            this.processNewsFeedResponse(response, true);
            items.length = 0;
            this.updateNewItemsCount();
        });
        return this.fiLoadingService.trackPromise(promise);
    }

    FocusWriteNewPost() {
        this.WriteNewPost = true;
        this.subscribeOnDocumentClick();
    }

    private subscribeOnDocumentClick() {
        if (this.documentClickSubscription) return;
        this.documentClickSubscription = this.fiBrowserEventsService
            .getDocumentEventSubject$('click')
            .subscribe(($event) => {
                const target = $event.target as HTMLElement;
                if (!target) {
                    return;
                }
                const isTargetClickIgnored =
                    findClosest(target, '.NewsFeedHeader') ||
                    target.matches('.tooltip') ||
                    (target.parentElement && target.parentElement.matches('.tooltip')) ||
                    target.matches('.CcmTextEditorAttachmentRemove') ||
                    target.matches('input[type=file]') ||
                    target.matches('.mat-mdc-option') ||
                    findClosest(target, '.cdk-dialog-container') ||
                    findClosest(target, '.cdk-overlay-backdrop') ||
                    findClosest(target, '.mat-mdc-select-panel') ||
                    findClosest(target, '.mat-mdc-autocomplete-panel');

                if (isTargetClickIgnored) return;

                if (!this.Post.Content?.length && !this.Post.MediaBankFileReferences?.length) {
                    this.WriteNewPost = false;
                    this.cdr.markForCheck();
                    this.unsubscribeClickSubscription();
                }
            });
    }

    AfterSavePost(savePost: PostModel) {
        const idTypeData = {
            ObjectId: savePost.Id,
            ObjectType: savePost.GetObjectType(),
        };
        this.fiLoadingService.trackPromise(
            this.newsFeedService.GetNewsFeedsByIdType([idTypeData]).then((data) => {
                this.processNewsFeedResponse(data, true);
                this.WriteNewPost = false;
                //this.Post.AccessRightFilterCollection.SafeDispatchEvent('accessRightFilterCollectionSaved');
                this.Post.Reset(this.fiFormatDateService);
                this.CanCreatePost = false;
                setTimeout(() => {
                    this.CanCreatePost = true;
                    document
                        .getElementById(`NewsFeedItem-${idTypeData.ObjectId}-${idTypeData.ObjectType}`)
                        .scrollIntoView({ behavior: 'smooth' });
                });
            }),
        );
    }

    cancelPostEdit(): void {
        this.WriteNewPost = false;
    }

    DeleteNewsFeedItem(itemToDelete: NewsFeedModel) {
        // https://chainformation.atlassian.net/browse/FI-2834
        this.newsFeedService.deleteNewsFeedItem(itemToDelete).then(() => {
            this.newsFeedItems
                .filter(
                    (item) => item.ObjectId === itemToDelete.ObjectId && item.ObjectType === itemToDelete.ObjectType,
                )
                .forEach((existingItem) => {
                    this.newsFeedItems.splice(this.newsFeedItems.indexOf(existingItem), 1);
                });
            this.cdr.markForCheck();
        });
    }

    private unsubscribeClickSubscription() {
        if (this.documentClickSubscription) {
            this.documentClickSubscription.unsubscribe();
            this.documentClickSubscription = undefined;
        }
    }
}
